import { useEffect, useRef } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';

/**
 * Keeps a consent checkbox in sync with its matching contact field(s) — e.g.
 * typing a phone or WhatsApp number auto-checks WhatsApp consent, typing an
 * email auto-checks Email consent — but a manual toggle by the user is
 * sticky: once they've touched the checkbox themselves, this stops
 * overriding it.
 *
 * Skips the very first run deliberately. On the create form the initial
 * contact fields are empty so this is a no-op either way, but on the edit
 * form the initial values already reflect the lead's PERSISTED consent
 * (which may be a real, intentional opt-out for a client who has a phone
 * number on file) — firing on mount would silently flip that back to
 * "consented" underneath the user before they've touched anything.
 */
export function useAutoConsentCheckbox<T extends FieldValues>(
  form: UseFormReturn<T>,
  contactFields: Path<T>[],
  consentField: Path<T>
) {
  const touched = useRef(false);
  const mounted = useRef(false);
  const contactValues = form.watch(contactFields) as unknown[];
  const hasContact = contactValues.some((value) =>
    Boolean((value as string | undefined)?.toString().trim())
  );

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (touched.current) return;
    form.setValue(consentField, hasContact as never, { shouldDirty: false });
  }, [hasContact, form, consentField]);

  return {
    /** Call from the checkbox's `onCheckedChange` so future auto-sync stops. */
    markTouched: () => {
      touched.current = true;
    },
  };
}
